import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Inbox, Heart, TrendingUp, TrendingDown, Minus, MessageSquare, Users } from "lucide-react";
import { API, fmtPrice } from "../../lib/store";
import { STATUS_COLORS, fmtDate } from "./adminShared";
import { Badge } from "../ui/badge";

function Delta({ now, prev }) {
  if (now > prev) return <span className="inline-flex items-center gap-1 text-[#3f6b3a] text-xs"><TrendingUp size={13} /> +{now - prev} vs last week</span>;
  if (now < prev) return <span className="inline-flex items-center gap-1 text-[#8c2f1b] text-xs"><TrendingDown size={13} /> {now - prev} vs last week</span>;
  return <span className="inline-flex items-center gap-1 text-[#9c9482] text-xs"><Minus size={13} /> same as last week</span>;
}

function Panel({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white border border-[#eee6d6] p-6" data-testid={testid}>
      <h3 className="font-heading text-lg text-[#171310] flex items-center gap-2 mb-4">
        <Icon size={17} className="text-[#a8842c]" /> {title}
      </h3>
      {children}
    </div>
  );
}

export function OverviewTab({ auth }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const token = auth.headers.Authorization;
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/overview`, { headers: { Authorization: token } });
      setData(r.data);
    } catch (e) {
      console.warn("Overview load failed:", e?.message);
      setError(true);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-[#8c2f1b] text-sm py-10 text-center">Could not load the overview — try Refresh.</p>;
  if (!data) return <p className="text-[#8f8571] text-sm py-10 text-center">Loading overview...</p>;

  const weekCards = [
    { label: "Inquiries This Week", v: data.week.inquiries, prev: data.week.inquiriesPrev },
    { label: "Bookings This Week", v: data.week.bookings, prev: data.week.bookingsPrev },
    { label: "New Subscribers This Week", v: data.week.subscribers },
    { label: "Signed-in Shoppers", v: data.totals.users },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* Week pulse */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {weekCards.map((c) => (
          <div key={c.label} className="bg-white border border-[#eee6d6] p-5">
            <p className="font-heading text-3xl text-[#171310]">{c.v}</p>
            <p className="text-[11px] tracking-[0.12em] uppercase text-[#8f8571] mt-1">{c.label}</p>
            {c.prev !== undefined && <div className="mt-2"><Delta now={c.v} prev={c.prev} /></div>}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming appointments */}
        <Panel title="Upcoming Appointments" icon={CalendarDays} testid="overview-upcoming">
          {data.upcomingBookings.length === 0 ? (
            <p className="text-sm text-[#8f8571]">No upcoming appointments.</p>
          ) : (
            <div className="divide-y divide-[#f2ecdd]">
              {data.upcomingBookings.map((b) => (
                <div key={b.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#171310] truncate">{b.name}</p>
                    <p className="text-xs text-[#5c5240] mt-0.5">{b.date} at {b.time}{b.interest ? ` · ${b.interest}` : ""}</p>
                  </div>
                  <Badge className={`rounded-none shrink-0 ${STATUS_COLORS[b.status]} hover:${STATUS_COLORS[b.status]}`}>{b.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Most inquired */}
        <Panel title="Most Inquired Pieces" icon={Inbox} testid="overview-top-inquired">
          {data.topInquired.length === 0 ? (
            <p className="text-sm text-[#8f8571]">No product inquiries yet.</p>
          ) : (
            <div className="divide-y divide-[#f2ecdd]">
              {data.topInquired.map((t) => (
                <div key={t.handle || t.product} className="py-3 flex items-center gap-3">
                  {t.image && <img src={t.image} alt="" loading="lazy" className="w-11 h-11 object-contain bg-[#faf7f0] shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#171310] truncate">{t.product}</p>
                    {t.price != null && <p className="text-xs text-[#8f8571]">{fmtPrice(t.price)}</p>}
                  </div>
                  <span className="text-xs text-[#a8842c] shrink-0">{t.count} {t.count === 1 ? "inquiry" : "inquiries"}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Wishlist favorites */}
        <Panel title="Wishlist Favorites" icon={Heart} testid="overview-wishlist">
          {data.wishlistFavorites.length === 0 ? (
            <p className="text-sm text-[#8f8571]">No signed-in shoppers have saved pieces yet.</p>
          ) : (
            <div className="divide-y divide-[#f2ecdd]">
              {data.wishlistFavorites.map((w) => (
                <div key={w.handle} className="py-3 flex items-center gap-3">
                  {w.image && <img src={w.image} alt="" loading="lazy" className="w-11 h-11 object-contain bg-[#faf7f0] shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#171310] truncate">{w.title}</p>
                    {w.price != null && <p className="text-xs text-[#8f8571]">{fmtPrice(w.price)}</p>}
                  </div>
                  <span className="text-xs text-[#a8842c] shrink-0 inline-flex items-center gap-1"><Heart size={12} className="fill-[#a8842c]" /> {w.count}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Recent inquiries */}
        <Panel title="Latest Inquiries" icon={MessageSquare} testid="overview-recent">
          {data.recentInquiries.length === 0 ? (
            <p className="text-sm text-[#8f8571]">No inquiries yet.</p>
          ) : (
            <div className="divide-y divide-[#f2ecdd]">
              {data.recentInquiries.map((q) => (
                <div key={q.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#171310] truncate">{q.name}{q.product ? <span className="font-normal text-[#8f8571]"> · {q.product}</span> : ""}</p>
                    <p className="text-xs text-[#9c9482] mt-0.5">{fmtDate(q.createdAt)}</p>
                  </div>
                  <Badge className={`rounded-none shrink-0 ${STATUS_COLORS[q.status]} hover:${STATUS_COLORS[q.status]}`}>{q.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <p className="text-xs text-[#9c9482] flex items-center gap-2">
        <Users size={13} /> Catalog: {data.productCount} products{data.lastSync ? ` · prices last synced ${fmtDate(data.lastSync)} (auto every 15 min)` : ""}
      </p>
    </div>
  );
}
